import React from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, X, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { money } from "../lib/ui";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQuantity, removeItem, subtotal, shipping, total } = useCart();
  const navigate = useNavigate();

  const checkout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-drawer"
        className="flex w-full flex-col border-l border-line bg-sand p-0 sm:max-w-md"
      >
        <SheetHeader className="border-b border-line px-6 py-5 text-left">
          <SheetTitle className="flex items-center gap-3 font-display text-3xl tracking-tight text-ink">
            Your Cart
            <span className="font-mono text-sm text-ink/40">[{items.length}]</span>
          </SheetTitle>
          <SheetDescription className="sr-only">Review the research peptides in your cart and proceed to checkout.</SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
            <ShoppingBag className="h-10 w-10 text-ink/30" strokeWidth={1} />
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-ink/50">Your cart is empty</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.map((item) => (
                <div key={item.slug} data-testid={`cart-item-${item.slug}`} className="flex gap-4 border-b border-line py-5">
                  <div className="h-20 w-20 shrink-0 overflow-hidden border border-line bg-surface">
                    <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                  </div>
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-display text-xl leading-none text-ink">{item.name}</p>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-ink/45">{item.size}</p>
                      </div>
                      <button
                        data-testid={`remove-${item.slug}`}
                        onClick={() => removeItem(item.slug)}
                        className="text-ink/40 transition-colors hover:text-rust"
                      >
                        <X className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="mt-auto flex items-center justify-between pt-3">
                      <div className="flex items-center border border-line">
                        <button data-testid={`dec-${item.slug}`} onClick={() => updateQuantity(item.slug, item.quantity - 1)} className="p-2 hover:bg-surface">
                          <Minus className="h-3 w-3" strokeWidth={2} />
                        </button>
                        <span className="w-8 text-center font-mono text-sm">{item.quantity}</span>
                        <button data-testid={`inc-${item.slug}`} onClick={() => updateQuantity(item.slug, item.quantity + 1)} className="p-2 hover:bg-surface">
                          <Plus className="h-3 w-3" strokeWidth={2} />
                        </button>
                      </div>
                      <span className="font-mono text-sm text-ink">{money(item.price * item.quantity)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-line bg-surface px-6 py-6">
              <div className="space-y-2 font-mono text-xs uppercase tracking-[0.15em] text-ink/70">
                <div className="flex justify-between"><span>Subtotal</span><span>{money(subtotal)}</span></div>
                <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? "Free" : money(shipping)}</span></div>
              </div>
              <div className="mt-4 flex justify-between border-t border-line pt-4">
                <span className="font-display text-2xl text-ink">Total</span>
                <span data-testid="cart-total" className="font-mono text-xl text-ink">{money(total)}</span>
              </div>
              <button
                data-testid="checkout-button"
                onClick={checkout}
                className="mt-6 w-full border border-ink bg-ink py-4 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-rust hover:border-rust"
              >
                Proceed to Checkout
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
